import { assign, fromPromise, setup, type AnyActorRef } from 'xstate';
import { firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';
import {
  ServicePlan,
  TripLegType,
  type Reservation,
  type ReservationPhone,
  type ReservationValidations,
} from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import reservationUtils from '@/services/reservationUtils';
import type { TripTypeContext } from './tripType.machine';
import { levelOfCareMachine } from './levelOfCare.machine';
import { passengerMobilityMachine } from './passengerMobility.machine';
import { passengerAssistanceMachine } from './passengerAssistance.machine';
import { standingOrderMachine } from './standingOrder.machine';
import { appointmentDateMachine } from './appointmentDate.machine';
import { appointmentDateTimeMachine } from './appointmentDateTime.machine';
import { dropoffAddressMachine } from './dropoffAddress.machine';
import { dropoffNotesMachine } from './dropoffNotes.machine';
import { dropoffPhoneMachine } from './dropoffPhone.machine';
import { pickupAddressMachine } from './pickupAddress.machine';
import { pickupDateMachine } from './pickupDate.machine';
import { pickupDateTimeMachine } from './pickupDateTime.machine';
import { pickupNotesMachine } from './pickupNotes.machine';
import { pickupPhoneMachine } from './pickupPhone.machine';
import { reasonForTripMachine } from './reasonForTrip.machine';
import { tripTypeMachine } from './tripType.machine';
import { willCallMachine } from './willCall.machine';
import { tripLegAddressConfidenceMachine } from './tripLegAddressConfidence.machine';
import {
  tripLegEstimatedPickupMachine,
  type GetDirectionsFn,
} from './tripLegEstimatedPickup.machine';
import type { FieldPath } from 'react-hook-form';
import {
  reservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
  type TripSegmentValues,
} from '../schema/reservationForm.schema';

import { shouldValidateReservation } from '../utils/shouldValidateReservation';
import { getTripsFromMachines } from '../utils/getTripsFromMachines';

export type ReservationFormMainContext = {
  tripTypeRef: AnyActorRef | null;
  reasonForTripRef: AnyActorRef | null;
  appointmentDateRef: AnyActorRef | null;
  appointmentDateTimeRef: AnyActorRef | null;
  pickupDateRef: AnyActorRef | null;
  pickupDateTimeRef: AnyActorRef | null;
  pickupTimeZoneRef: AnyActorRef | null;
  dropoffTimeZoneRef: AnyActorRef | null;
  pickupAddressRef: AnyActorRef | null;
  dropoffAddressRef: AnyActorRef | null;
  pickupPhoneRef: AnyActorRef | null;
  dropoffPhoneRef: AnyActorRef | null;
  pickupNotesRef: AnyActorRef | null;
  dropoffNotesRef: AnyActorRef | null;
  willCallRef: AnyActorRef | null;
  standingOrderRef: AnyActorRef | null;
  levelOfCareRef: AnyActorRef | null;
  passengerMobilityRef: AnyActorRef | null;
  passengerAssistanceRef: AnyActorRef | null;
  tripLegAddressConfidenceRef: AnyActorRef | null;
  /** First trip leg (`tripIndex` 0); subscribes to pickup/dropoff address array actors. */
  tripLegEstimatedPickupRef: AnyActorRef | null;
  clientBC: string;
  passengerUUID: string;
  passengerPhone?: ReservationPhone;
  submitFormValues?: ReservationFormValues;
  saveAsDraft?: boolean;
  servicePlans: ServicePlan[];
  lastValidationResult: ReservationValidations | null;
  getDirections?: GetDirectionsFn;
  /**
   * Returns the current handler to sync validation trip-leg updates into React Hook Form.
   * Implemented as a getter so the machine keeps a stable reference while the form updates.
   */
  applyTripLegValidationResultsToForm?: () => (
    validations: ReservationValidations,
  ) => void;
};

export type ReservationFormMainInput = {
  clientBC: string;
  passengerUUID: string;
  passengerPhone?: ReservationPhone;
  getDirections?: GetDirectionsFn;
  applyTripLegValidationResultsToForm?: () => (
    validations: ReservationValidations,
  ) => void;
};

export type ReservationFormMainEvent =
  | { type: 'SUBMIT'; formValues: ReservationFormValues }
  | {
      type: 'FIELD_EDITED';
      reservation?: unknown;
      /** React Hook Form field path from the last `watch` notification before send (debounced). */
      editedField?: FieldPath<ReservationFormInput>;
    }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'SAVE_AS_DRAFT'; formValues: ReservationFormValues }
  | { type: 'SAVE_AS_DRAFT_SUCCESS' }
  | { type: 'SAVE_AS_DRAFT_ERROR' }
  | { type: 'OVERRIDE_OPEN' }
  | { type: 'OVERRIDE_OPEN_SUCCESS' }
  | { type: 'OVERRIDE_OPEN_ERROR' }
  | { type: 'VALIDATION_SUCCESS' }
  | { type: 'VALIDATION_ERROR' }
  | { type: 'TRIP_VALIDATION_SUCCESS' }
  | { type: 'TRIP_VALIDATION_ERROR' }
  | { type: 'ADDRESS_VALIDATION_SUCCESS' }
  | { type: 'ADDRESS_VALIDATION_ERROR' }
  | { type: 'RESET_ALL' }
  | { type: 'SET_TRIP_COUNT'; count: number }
  | {
      type: 'SET_PASSENGER';
      clientBC: string;
      passengerUUID: string;
      passengerPhone?: ReservationPhone;
    };

function readTripTypeField(ref: AnyActorRef | null): TripLegType {
  if (!ref) return TripLegType.OneWay;
  const ctx = ref.getSnapshot().context as TripTypeContext;
  return ctx.value;
}

export function getAggregatedFormValues(
  context: ReservationFormMainContext,
): ReservationFormValues {
  return {
    tripType: readTripTypeField(context.tripTypeRef),
    genericErrors: '',
    trips: getTripsFromMachines(context),
  };
}

const initialContext: ReservationFormMainContext = {
  tripTypeRef: null,
  reasonForTripRef: null,
  appointmentDateRef: null,
  appointmentDateTimeRef: null,
  pickupDateRef: null,
  pickupDateTimeRef: null,
  pickupTimeZoneRef: null,
  dropoffTimeZoneRef: null,
  pickupAddressRef: null,
  dropoffAddressRef: null,
  pickupPhoneRef: null,
  dropoffPhoneRef: null,
  pickupNotesRef: null,
  dropoffNotesRef: null,
  willCallRef: null,
  standingOrderRef: null,
  levelOfCareRef: null,
  passengerMobilityRef: null,
  passengerAssistanceRef: null,
  tripLegAddressConfidenceRef: null,
  tripLegEstimatedPickupRef: null,
  clientBC: '',
  passengerUUID: '',
  servicePlans: [],
  lastValidationResult: null,
  applyTripLegValidationResultsToForm: undefined,
};

function canSubmitFromContext(context: ReservationFormMainContext): boolean {
  const aggregated = getAggregatedFormValues(context);
  const parsed = reservationFormSchema.safeParse(aggregated);
  return parsed.success;
}

function isFieldEditedWithReservation(
  event: ReservationFormMainEvent,
): event is {
  type: 'FIELD_EDITED';
  reservation: unknown;
  editedField?: FieldPath<ReservationFormInput>;
} {
  return (
    event.type === 'FIELD_EDITED' &&
    'reservation' in event &&
    event.reservation !== undefined &&
    event.reservation !== null
  );
}

function isTripAddressFilled(
  address:
    | TripSegmentValues['pickupAddress']
    | TripSegmentValues['dropoffAddress'],
): boolean {
  if (typeof address === 'string') {
    return address.trim().length > 0;
  }
  if (address && typeof address === 'object') {
    if (address.isHeader === true || address.place_id === 'header') {
      return false;
    }
    return Boolean(
      address.place_id?.trim() ||
      address.mainText?.trim() ||
      address.description?.trim(),
    );
  }
  return false;
}

function tripLegMeetsDraftRequirements(trip: TripSegmentValues): boolean {
  if (!String(trip.reasonForTrip ?? '').trim()) {
    return false;
  }

  if (trip.isWillCall) {
    if (!String(trip.appointmentDate ?? '').trim()) {
      return false;
    }
  } else {
    const appt = trip.appointmentDateTime;
    if (appt == null || !(appt instanceof DateTime && appt.isValid)) {
      return false;
    }
  }

  if (!trip.isWillCall) {
    const pickup = trip.pickupDateTime;
    if (pickup == null || !(pickup instanceof DateTime && pickup.isValid)) {
      return false;
    }
  }

  if (!isTripAddressFilled(trip.pickupAddress)) {
    return false;
  }
  if (!isTripAddressFilled(trip.dropoffAddress)) {
    return false;
  }
  if (!String(trip.AttributeLevel1 ?? '').trim()) {
    return false;
  }
  if (!String(trip.MobilityAttribute ?? '').trim()) {
    return false;
  }

  return true;
}

export function canSaveAsDraftFromFormValues(
  formValues: ReservationFormValues,
): boolean {
  if (!formValues.trips?.length) {
    return false;
  }
  return formValues.trips.every(tripLegMeetsDraftRequirements);
}

/** True when every trip leg has purpose, appointment, pickup time (or will-call), addresses, AttributeLevel1, and MobilityAttribute. */
export function canSaveAsDraftFromContext(
  context: ReservationFormMainContext,
): boolean {
  return canSaveAsDraftFromFormValues(getAggregatedFormValues(context));
}

export const reservationFormMainMachine = setup({
  types: {
    context: {} as ReservationFormMainContext,
    events: {} as ReservationFormMainEvent,
    input: {} as ReservationFormMainInput,
  },
  actors: {
    createReservationRequest: fromPromise<
      Reservation,
      {
        clientBC: string;
        passengerUUID: string;
        passengerPhone?: ReservationPhone;
        formValues: ReservationFormValues;
        saveAsDraft: boolean;
      }
    >(async ({ input }) => {
      const { reservation, standingOrder } =
        reservationUtils.getCreateReservationPayloadFromReservationForm(
          input.clientBC,
          input.passengerUUID,
          input.formValues,
          input.passengerPhone,
        );
      return firstValueFrom(
        reservationService.createReservation(
          reservation,
          standingOrder,
          input.saveAsDraft,
        ),
      );
    }),
    validateReservation: fromPromise<
      ReservationValidations,
      { reservation: unknown }
    >(async ({ input, signal }) => {
      if (input.reservation == null) {
        throw new Error('validateReservation: missing reservation payload');
      }
      return firstValueFrom(
        reservationService.validateReservation(input.reservation, signal),
      );
    }),
    tripType: tripTypeMachine,
    reasonForTrip: reasonForTripMachine,
    appointmentDate: appointmentDateMachine,
    appointmentDateTime: appointmentDateTimeMachine,
    pickupDate: pickupDateMachine,
    pickupDateTime: pickupDateTimeMachine,
    pickupAddress: pickupAddressMachine,
    dropoffAddress: dropoffAddressMachine,
    pickupPhone: pickupPhoneMachine,
    dropoffPhone: dropoffPhoneMachine,
    pickupNotes: pickupNotesMachine,
    dropoffNotes: dropoffNotesMachine,
    willCall: willCallMachine,
    standingOrder: standingOrderMachine,
    levelOfCare: levelOfCareMachine,
    passengerMobility: passengerMobilityMachine,
    passengerAssistance: passengerAssistanceMachine,
    tripLegAddressConfidence: tripLegAddressConfidenceMachine,
    tripLegEstimatedPickup: tripLegEstimatedPickupMachine,
  },
  actions: {
    syncTripCount: ({
      context,
      event,
    }: {
      context: ReservationFormMainContext;
      event: ReservationFormMainEvent;
    }) => {
      if (event.type !== 'SET_TRIP_COUNT') return;
      const count = Math.max(1, event.count);
      const refs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.levelOfCareRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      refs.forEach((r) => r?.send({ type: 'RESIZE', count }));
    },
    resetAllFields: ({ context }: { context: ReservationFormMainContext }) => {
      context.tripTypeRef?.send({ type: 'RESET' });
      context.reasonForTripRef?.send({ type: 'RESET' });
      const tripFieldRefs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      tripFieldRefs.forEach((r) => r?.send({ type: 'RESET' }));
      context.levelOfCareRef?.send({ type: 'RESET' });
      context.tripLegAddressConfidenceRef?.send({ type: 'RESET' });
      context.tripLegEstimatedPickupRef?.send({ type: 'RESET' });
    },
    updateFormValuesFromValidationResult: ({ context, event }) => {
      if (!('output' in event)) return;
      const validations = event.output as ReservationValidations;
      if (validations == null) return;
      context.applyTripLegValidationResultsToForm?.()?.(validations);
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        const trip = context.submitFormValues?.trips[idx];
        if (trip == null) continue;
        if (update.pickUpAddress?.timeZone) {
          trip.pickupTimeZone = update.pickUpAddress.timeZone;
        }
        if (update.dropOffAddress?.timeZone) {
          trip.dropoffTimeZone = update.dropOffAddress.timeZone;
        }
      }
    },
  },
  guards: {
    canSubmit: ({ context }) => canSubmitFromContext(context),
    canSubmitFromSubmitEvent: ({ context, event }) => {
      if (event.type !== 'SUBMIT' && event.type !== 'SAVE_AS_DRAFT') {
        return false;
      }
      if (!context.clientBC?.trim() || !context.passengerUUID?.trim()) {
        return false;
      }
      const parsed = reservationFormSchema.safeParse(event.formValues);
      return parsed.success;
    },
    canSaveAsDraftFromEvent: ({ context, event }) => {
      if (event.type !== 'SAVE_AS_DRAFT') {
        return false;
      }
      if (!context.clientBC?.trim() || !context.passengerUUID?.trim()) {
        return false;
      }
      return canSaveAsDraftFromFormValues(event.formValues);
    },
    canSaveAsDraft: ({ context }) => canSaveAsDraftFromContext(context),
    shouldValidateAndHasReservation: ({ context, event }) =>
      shouldValidateReservation(context, event) && isFieldEditedWithReservation(event),
  },
}).createMachine({
  id: 'reservationFormMain',
  context: ({ input }) => ({
    ...initialContext,
    clientBC: input?.clientBC ?? '',
    passengerUUID: input?.passengerUUID ?? '',
    passengerPhone: input?.passengerPhone,
    getDirections: input?.getDirections,
    applyTripLegValidationResultsToForm:
      input?.applyTripLegValidationResultsToForm,
  }),
  initial: 'bootstrap',
  states: {
    bootstrap: {
      entry: assign(({ spawn, context }) => {
        const pickupAddressRef = spawn('pickupAddress', {
          id: 'pickupAddress',
        });
        const dropoffAddressRef = spawn('dropoffAddress', {
          id: 'dropoffAddress',
        });
        return {
          tripTypeRef: spawn('tripType', { id: 'tripType' }),
          reasonForTripRef: spawn('reasonForTrip', { id: 'reasonForTrip' }),
          appointmentDateRef: spawn('appointmentDate', {
            id: 'appointmentDate',
          }),
          appointmentDateTimeRef: spawn('appointmentDateTime', {
            id: 'appointmentDateTime',
          }),
          pickupDateRef: spawn('pickupDate', { id: 'pickupDate' }),
          pickupDateTimeRef: spawn('pickupDateTime', { id: 'pickupDateTime' }),
          pickupAddressRef,
          dropoffAddressRef,
          tripLegAddressConfidenceRef: spawn('tripLegAddressConfidence', {
            id: 'tripLegAddressConfidence',
            input: { pickupAddressRef, dropoffAddressRef },
          }),
          tripLegEstimatedPickupRef: spawn('tripLegEstimatedPickup', {
            id: 'tripLegEstimatedPickup',
            input: {
              pickupAddressRef,
              dropoffAddressRef,
              getDirections: context.getDirections,
            },
          }),
          pickupPhoneRef: spawn('pickupPhone', { id: 'pickupPhone' }),
          dropoffPhoneRef: spawn('dropoffPhone', { id: 'dropoffPhone' }),
          pickupNotesRef: spawn('pickupNotes', { id: 'pickupNotes' }),
          dropoffNotesRef: spawn('dropoffNotes', { id: 'dropoffNotes' }),
          willCallRef: spawn('willCall', { id: 'willCall' }),
          standingOrderRef: spawn('standingOrder', { id: 'standingOrder' }),
          levelOfCareRef: spawn('levelOfCare', { id: 'levelOfCare' }),
          passengerMobilityRef: spawn('passengerMobility', {
            id: 'passengerMobility',
          }),
          passengerAssistanceRef: spawn('passengerAssistance', {
            id: 'passengerAssistance',
          }),
        };
      }),
      always: { target: 'editing' },
    },
    editing: {
      on: {
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
        },
        SUBMIT: {
          target: 'submitting',
          guard: 'canSubmitFromSubmitEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SUBMIT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SUBMIT' ? false : undefined,
          }),
        },
        SAVE_AS_DRAFT: {
          target: 'submitting',
          guard: 'canSaveAsDraftFromEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? true : undefined,
          }),
        },
        RESET_ALL: { actions: 'resetAllFields' },
      },
    },
    validating: {
      on: {
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
          reenter: true,
        },
      },
      invoke: {
        id: 'validateReservation',
        src: 'validateReservation',
        input: ({ event }) => {
          if (!isFieldEditedWithReservation(event)) {
            return { reservation: null };
          }
          return { reservation: event.reservation };
        },
        onDone: {
          target: 'editing',
          actions: [
            ({ context, event }) =>
              context.applyTripLegValidationResultsToForm?.()?.(
                event.output as ReservationValidations,
              ),
            'updateFormValuesFromValidationResult',
            assign({
              lastValidationResult: ({ event }) => event.output,
            }),
          ],
        },
        onError: { target: 'editing' },
      },
    },
    submitting: {
      invoke: {
        id: 'createReservationRequest',
        src: 'createReservationRequest',
        input: ({ context }) => ({
          clientBC: context.clientBC,
          passengerUUID: context.passengerUUID,
          passengerPhone: context.passengerPhone,
          formValues: context.submitFormValues!,
          saveAsDraft: context.saveAsDraft ?? false,
        }),
        onDone: { target: 'success' },
        onError: { target: 'editing' },
      },
    },
    success: {
      on: {
        RESET_ALL: {
          target: 'editing',
          actions: [
            'resetAllFields',
            assign({ lastValidationResult: () => null }),
          ],
        },
      },
    },
  },
  on: {
    SET_TRIP_COUNT: { actions: 'syncTripCount' },
    SET_PASSENGER: {
      actions: assign({
        clientBC: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.clientBC : '',
        passengerUUID: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerUUID : '',
        passengerPhone: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerPhone : undefined,
      }),
    },
  },
});
